import React, { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";
import { useSettingsMutation } from "@/api/mutations/admin/SettingsMutations";
import { Shield, Upload, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

const SSLSettingsSection: React.FC = () => {
    const { t } = useTranslation();
    const { data: settings } = useSettingsQuery(true);
    const { mutate: updateSettings } = useSettingsMutation();
    
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [privateKeyFile, setPrivateKeyFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    
    const certificateInputRef = useRef<HTMLInputElement>(null);
    const privateKeyInputRef = useRef<HTMLInputElement>(null);

    // Get SSL certificate status
    const { data: certificateStatus, refetch: refetchStatus } = trpc.sslCertificate.checkCertificate.useQuery();
    
    // Upload mutation
    const uploadMutation = trpc.sslCertificate.uploadCertificate.useMutation({
        onSuccess: (data) => {
            if (data.success) {
                toast.success(t("settings.sslSettings.messages.uploadSuccess"));
                refetchStatus();
                setCertificateFile(null);
                setPrivateKeyFile(null);
                if (certificateInputRef.current) certificateInputRef.current.value = "";
                if (privateKeyInputRef.current) privateKeyInputRef.current.value = "";
            } else {
                toast.error(data.message);
            }
            setIsUploading(false);
        },
        onError: (error) => {
            toast.error(error.message || t("settings.sslSettings.messages.uploadFailed"));
            setIsUploading(false);
        },
    });

    // Delete mutation
    const deleteMutation = trpc.sslCertificate.deleteCertificate.useMutation({
        onSuccess: (data) => {
            if (data.success) {
                toast.success(t("settings.sslSettings.messages.deleteSuccess"));
                refetchStatus();
                // Disable SSL in settings
                updateSettings({
                    branding_settings: {
                        ...(settings as any)?.branding_settings,
                        ssl_enabled: false,
                    },
                });
            } else {
                toast.error(data.message);
            }
        },
        onError: (error) => {
            toast.error(error.message || t("settings.sslSettings.messages.deleteFailed"));
        },
    });

    const handleUpload = async () => {
        if (!certificateFile || !privateKeyFile) {
            toast.error(t("settings.sslSettings.messages.bothFilesRequired"));
            return;
        }

        setIsUploading(true);

        try {
            const certificateText = await certificateFile.text();
            const privateKeyText = await privateKeyFile.text();

            uploadMutation.mutate({
                certificate: certificateText,
                privateKey: privateKeyText,
            });
        } catch (error: any) {
            toast.error(`${t("settings.sslSettings.messages.readFilesFailed")} ${error.message}`);
            setIsUploading(false);
        }
    };

    const handleToggleSSL = (enabled: boolean) => {
        if (enabled && !certificateStatus?.exists) {
            toast.error(t("settings.sslSettings.messages.uploadCertificateFirst"));
            return;
        }

        updateSettings({
            branding_settings: {
                ...(settings as any)?.branding_settings,
                ssl_enabled: enabled,
            },
        });
    };

    const sslEnabled = (settings as any)?.branding_settings?.ssl_enabled ?? false;

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Shield className="h-6 w-6" />
                    {t("settings.sslSettings.title")}
                </h2>
                <p className="text-base text-muted-foreground">
                    {t("settings.sslSettings.description")}
                </p>
            </div>

            {/* Status Card */}
            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.sslSettings.certificateStatus.title")}</CardTitle>
                    <CardDescription>{t("settings.sslSettings.certificateStatus.description")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {certificateStatus?.exists ? (
                        <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
                            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                            <AlertDescription className="text-green-800 dark:text-green-200 mt-1">
                                {t("settings.sslSettings.certificateStatus.installed")}
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <Alert className="border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                            <AlertDescription className="text-yellow-800 dark:text-yellow-200 mt-1">
                                {t("settings.sslSettings.certificateStatus.notInstalled")}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Certificate Details */}
                    {certificateStatus?.exists && certificateStatus?.details && (
                        <div className="space-y-3 pt-4 border-t">
                            <h4 className="text-sm font-semibold">{t("settings.sslSettings.certificateDetails.title")}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.commonName")}</div>
                                    <div className="font-mono text-xs break-all">
                                        {certificateStatus.details.commonName || t("settings.sslSettings.certificateDetails.na")}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.issuer")}</div>
                                    <div className="font-mono text-xs break-all">
                                        {certificateStatus.details.issuer || t("settings.sslSettings.certificateDetails.na")}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.validFrom")}</div>
                                    <div className="font-mono text-xs">
                                        {certificateStatus.details.validFrom || t("settings.sslSettings.certificateDetails.na")}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.validTo")}</div>
                                    <div className="font-mono text-xs">
                                        {certificateStatus.details.validTo || t("settings.sslSettings.certificateDetails.na")}
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.fingerprint")}</div>
                                    <div className="font-mono text-xs break-all">
                                        {certificateStatus.details.fingerprint || t("settings.sslSettings.certificateDetails.na")}
                                    </div>
                                </div>
                                {certificateStatus.details.subjectAltNames && 
                                 certificateStatus.details.subjectAltNames.length > 0 && (
                                    <div className="md:col-span-2">
                                        <div className="text-muted-foreground">{t("settings.sslSettings.certificateDetails.subjectAltNames")}</div>
                                        <div className="font-mono text-xs break-all">
                                            {certificateStatus.details.subjectAltNames.join(", ")}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <div className="text-sm font-medium">{t("settings.sslSettings.enableHttps.title")}</div>
                            <div className="text-xs text-muted-foreground">
                                {t("settings.sslSettings.enableHttps.description")}
                            </div>
                        </div>
                        <Switch
                            checked={sslEnabled}
                            onCheckedChange={handleToggleSSL}
                            disabled={!certificateStatus?.exists}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Upload Card */}
            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.sslSettings.uploadCertificate.title")}</CardTitle>
                    <CardDescription>
                        {t("settings.sslSettings.uploadCertificate.description")}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Certificate File */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t("settings.sslSettings.uploadCertificate.certificateFile")}</label>
                        <div className="flex items-center gap-3">
                            <input
                                ref={certificateInputRef}
                                type="file"
                                accept=".crt,.pem,.cer"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setCertificateFile(file);
                                }}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => certificateInputRef.current?.click()}
                                className="flex-shrink-0"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t("settings.sslSettings.uploadCertificate.chooseFile")}
                            </Button>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">
                                    {certificateFile ? certificateFile.name : t("settings.sslSettings.uploadCertificate.noFileSelected")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Private Key File */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t("settings.sslSettings.uploadCertificate.privateKeyFile")}</label>
                        <div className="flex items-center gap-3">
                            <input
                                ref={privateKeyInputRef}
                                type="file"
                                accept=".key,.pem"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setPrivateKeyFile(file);
                                }}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => privateKeyInputRef.current?.click()}
                                className="flex-shrink-0"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {t("settings.sslSettings.uploadCertificate.chooseFile")}
                            </Button>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">
                                    {privateKeyFile ? privateKeyFile.name : t("settings.sslSettings.uploadCertificate.noFileSelected")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Upload Button */}
                    <div className="flex items-center gap-3">
                        <Button
                            onClick={handleUpload}
                            disabled={!certificateFile || !privateKeyFile || isUploading}
                        >
                            {isUploading ? t("settings.sslSettings.uploadCertificate.uploading") : t("settings.sslSettings.uploadCertificate.uploadButton")}
                        </Button>

                        {certificateStatus?.exists && (
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    if (confirm(t("settings.sslSettings.uploadCertificate.confirmDelete"))) {
                                        deleteMutation.mutate();
                                    }
                                }}
                            >
                                <XCircle className="h-4 w-4 mr-2" />
                                {t("settings.sslSettings.uploadCertificate.deleteCertificate")}
                            </Button>
                        )}
                    </div>

                    {/* Info Alert */}
                    <Alert>
                        <AlertDescription className="text-sm">
                            <strong>{t("settings.sslSettings.uploadCertificate.noteTitle")}</strong> {t("settings.sslSettings.uploadCertificate.noteDescription")}
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        </div>
    );
};

export default SSLSettingsSection;
